import React, { useEffect, useState, useContext } from 'react';
import { requestTradingData } from 'api/socketService';
import { useWalletData } from 'context/WalletDataContext';
import { useTradeUpdate } from 'context/TradeUpdateContext';
import { formatCurrency } from 'utils';
import { spotPerformanceByBase } from 'utils/tradePerformance';
import type { TradePerformance } from 'utils/tradePerformance';
import SocketContext from 'utils/hooks/useSocket';
import { CASH_TICKER, isCash } from 'utils/cash';

import {
  Wrap,
  Td,
  Th,
  Table,
  Tab,
  Button,
  ScrollWrap,
  Tr,
  PairCell,
  PairIcon,
  TypeBadge,
  Empty,
} from './styled';

type Trade = {
  id: number;
  trading_pair: string;
  amount: number;
  price: number;
  market_value: number;
  type: string;
  createdAt: string;
  updatedAt: string;
};

const TradeHistory = (props: { tournamentDetails: any; }) => {
  const [activeTab, setActiveTab] = useState("tab1");
  const { tournamentDetails } = props;
  const [tradingData, setTradingData] = useState<Trade[]>([]);
  const { walletData } = useWalletData();
  const { tradeUpdated } = useTradeUpdate();
  const { allCoinsValues } = useContext(SocketContext);

  const updateTrading = (data: any) => {
    if (data.success) {
      setTradingData(data.data.trading);
    } else {
      console.error('Error fetching trading data:');
    }
  };

  const fetchTradingData = () => {
    // TB-RULE-DATA-NORMALIZE — tournament payloads come back flat after the
    // Strapi v5 migration (no `attributes` wrapper). Reading `data.attributes.*`
    // alone silently returns undefined, the request is skipped, and the UI
    // shows Trade History (0) forever even though the backend has rows.
    const rawData: any = tournamentDetails?.data;
    const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
    const tournamentId = rawData?.id ?? attributes?.id;
    const tradeType = attributes?.trade_type;
    if (tournamentId && tradeType) {
      requestTradingData(tournamentId, tradeType, updateTrading);
    }
  };

  const [pairTradesData, updatePairTradesData] = useState<Record<string, TradePerformance> | null>(null);
  const [currentWalletData, updateCurrentWalletData] = useState<any[] | null>(null);

  if (walletData?.length) {
    if (currentWalletData?.length) {
      walletData.forEach((element: any, i: number) => {
        if (element?.amount !== currentWalletData[i]?.amount) {
          updateCurrentWalletData(walletData);
        }
      });
    } else {
      updateCurrentWalletData(walletData);
    }
  }

  useEffect(() => {
    fetchTradingData();
  }, [tradeUpdated]);

  const tradingPairWithAmount = walletData.filter(
    (obj: any) => obj.amount > 0 && !isCash(obj),
  );

  useEffect(() => {
    updatePairTradesData(spotPerformanceByBase(tradingData, allCoinsValues));
  }, [allCoinsValues, tradingData]);

  return (
    <Wrap>
      <Tab>
        <Button className={activeTab === 'tab1' ? 'active' : ''} onClick={() => setActiveTab('tab1')}>
          Trade History ({tradingData.length})
        </Button>
        <Button className={activeTab === 'tab2' ? 'active' : ''} onClick={() => setActiveTab('tab2')}>
          Asset Performance ({tradingPairWithAmount.length})
        </Button>
      </Tab>

      {activeTab === 'tab1' && (
        <ScrollWrap>
          {tradingData.length === 0 ? (
            <Empty>No trades yet</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Pair</Th>
                  <Th className="right">Amount</Th>
                  <Th className="right">Price</Th>
                  <Th className="right">Value</Th>
                  <Th>Type</Th>
                  <Th className="right">Time (UTC)</Th>
                </tr>
              </thead>
              <tbody>
                {tradingData.map((trade) => {
                  const coin = trade.trading_pair.split('/')[0];
                  return (
                    <Tr key={trade.id}>
                      <Td>
                        <PairCell>
                          <PairIcon $src={`../icons/tradingPair/use/${coin}.svg`} />
                          {trade.trading_pair}
                        </PairCell>
                      </Td>
                      <Td className="right">{trade.amount.toFixed(8)}</Td>
                      <Td className="right muted">{formatCurrency(trade.market_value)}</Td>
                      <Td className="right">{formatCurrency(trade.amount * trade.market_value)}</Td>
                      <Td>
                        <TypeBadge $type={trade.type === 'buy' ? 'buy' : 'sell'}>
                          {trade.type}
                        </TypeBadge>
                      </Td>
                      <Td className="right muted">{new Date(trade.createdAt).toLocaleString()}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </ScrollWrap>
      )}

      {activeTab === 'tab2' && (
        <ScrollWrap>
          {tradingPairWithAmount.length === 0 ? (
            <Empty>No assets held</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Asset</Th>
                  <Th className="right">Amount</Th>
                  <Th className="right">AVG Price</Th>
                  <Th className="right">P/L {CASH_TICKER}</Th>
                  <Th className="right">P/L%</Th>
                </tr>
              </thead>
              <tbody>
                {tradingPairWithAmount.map((pair: any) => {
                  const data = pairTradesData?.[pair.ticker.short_name];
                  const coin = pair.ticker.short_name;
                  const plPos = (data?.plPercent ?? 0) >= 0;
                  return (
                    <Tr key={pair.id}>
                      <Td>
                        <PairCell>
                          <PairIcon $src={`../icons/tradingPair/use/${coin}.svg`} />
                          {data ? `${formatCurrency(data.currentValue)} ${CASH_TICKER}` : '—'}
                        </PairCell>
                      </Td>
                      <Td className="right">
                        {data ? `${data.totalCoins.toFixed(8)} ${coin}` : '—'}
                      </Td>
                      <Td className="right muted">
                        {data ? `${formatCurrency(data.avgPrice.toFixed(2))} ${CASH_TICKER}` : '—'}
                      </Td>
                      <Td className={`right ${plPos ? 'up_state' : 'down_state'}`}>
                        {data ? `${formatCurrency(data.plUSDT)} ${CASH_TICKER}` : '—'}
                      </Td>
                      <Td className={`right ${plPos ? 'up_state' : 'down_state'}`}>
                        {data ? `${data.plPercent.toFixed(2)}%` : '—'}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </ScrollWrap>
      )}
    </Wrap>
  );
};

export default TradeHistory;
